import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../i18n';
import {
  IconWrench, IconPlus, IconMail, IconTrash, IconSearch,
  IconMapPin, IconBolt, IconHandshake, IconAlertTriangle,
} from '../components/admin/icons';

interface InstallerLink {
  linkId: number;
  installerId: number;
  name: string;
  city: string | null;
  speciality: string | null;
  contactEmail: string | null;
  addedAt: string;
}

interface DirectoryInstaller {
  id: number;
  name: string;
  city: string | null;
  speciality: string | null;
  contactEmail: string | null;
}

export default function MyInstallersPage() {
  const { token } = useAuth();
  const { t, locale } = useI18n();
  const [installers, setInstallers] = useState<InstallerLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<InstallerLink | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [revokeError, setRevokeError] = useState('');

  const API = import.meta.env.VITE_WATTELZCE_API_URL || '/api/wattelzce';

  useEffect(() => { fetchInstallers(); }, []);

  async function fetchInstallers() {
    try {
      const res = await fetch(`${API}/api/my-installers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setInstallers(data.installers);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  async function handleRevoke(linkId: number) {
    setRevokeError('');
    setRevoking(true);
    try {
      const res = await fetch(`${API}/api/my-installers/${linkId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'Révoqué par le client' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        const msg = typeof data?.error === 'string'
          ? data.error
          : data?.error?.message || t('team_errorStatus', { status: res.status });
        setRevokeError(msg);
        setRevoking(false);
        return;
      }
      setInstallers(prev => prev.filter(i => i.linkId !== linkId));
      setRevokeTarget(null);
    } catch (e: any) {
      setRevokeError(e?.message || t('team_networkError'));
    } finally {
      setRevoking(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hero — image installateurs en fond + overlay teal */}
      <div className="relative overflow-hidden rounded-3xl text-white shadow-2xl">
        {/* Image de fond (technicien / artisan installation) */}
        <img
          src="https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=1600&h=600&fit=crop"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-900/85 to-teal-900/50" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-transparent to-transparent" />
        {/* Pattern grille */}
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
          }}
        />
        {/* Halos */}
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-teal-500/30 blur-3xl" />
        <div className="absolute -bottom-32 -right-24 w-96 h-96 rounded-full bg-cyan-500/25 blur-3xl" />

        <div className="relative p-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-start gap-5">
              <div className="relative shrink-0">
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-teal-400 to-cyan-500 blur-lg opacity-60" />
                <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center shadow-lg ring-1 ring-white/20 text-white">
                  <IconWrench className="w-8 h-8" />
                </div>
              </div>
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/[0.08] backdrop-blur ring-1 ring-cyan-400/30 text-cyan-300 text-[11px] font-semibold mb-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
                  {t('team_inst_badge')}
                </div>
                <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-white drop-shadow-lg">
                  {t('team_inst_titleMy')}{' '}
                  <span className="bg-gradient-to-r from-teal-300 via-cyan-300 to-sky-300 bg-clip-text text-transparent">
                    {t('team_inst_titleInstallers')}
                  </span>
                </h1>
                <p className="text-slate-200 text-sm mt-1.5 drop-shadow">
                  {t('team_inst_subtitle')}
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowModal(true)}
              disabled={installers.length >= 1}
              title={installers.length >= 1 ? t('team_inst_alreadyOne') : ''}
              className="px-5 py-3 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-white font-semibold shadow-lg shadow-cyan-500/30 transition-all inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:from-teal-500 disabled:hover:to-cyan-500"
            >
              <IconPlus className="w-4 h-4" /> {t('team_inst_add')}
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-8">
            <div className="relative overflow-hidden rounded-2xl border border-teal-400/30 bg-white/[0.04] backdrop-blur-xl p-4">
              <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-teal-400/20 blur-2xl" />
              <div className="relative">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-teal-400/30 to-teal-500/10 ring-1 ring-teal-400/40 flex items-center justify-center text-teal-300">
                  <IconHandshake className="w-5 h-5" />
                </div>
                <p className="text-3xl font-bold text-white tracking-tight mt-3">{installers.length}</p>
                <p className="text-[11px] text-white/70 uppercase tracking-[0.15em] font-semibold mt-0.5">{t('team_inst_statLinked')}</p>
              </div>
            </div>
            <div className="relative overflow-hidden rounded-2xl border border-cyan-400/30 bg-white/[0.04] backdrop-blur-xl p-4">
              <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-cyan-400/20 blur-2xl" />
              <div className="relative">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-cyan-400/30 to-cyan-500/10 ring-1 ring-cyan-400/40 flex items-center justify-center text-cyan-300">
                  <IconMapPin className="w-5 h-5" />
                </div>
                <p className="text-3xl font-bold text-white tracking-tight mt-3">
                  {new Set(installers.map(i => i.city).filter(Boolean)).size}
                </p>
                <p className="text-[11px] text-white/70 uppercase tracking-[0.15em] font-semibold mt-0.5">{t('team_inst_statZones')}</p>
              </div>
            </div>
            <div className="relative overflow-hidden rounded-2xl border border-emerald-400/30 bg-white/[0.04] backdrop-blur-xl p-4">
              <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-emerald-400/20 blur-2xl" />
              <div className="relative">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-400/30 to-emerald-500/10 ring-1 ring-emerald-400/40 flex items-center justify-center text-emerald-300">
                  <IconBolt className="w-5 h-5" />
                </div>
                <p className="text-3xl font-bold text-white tracking-tight mt-3">24/7</p>
                <p className="text-[11px] text-white/70 uppercase tracking-[0.15em] font-semibold mt-0.5">{t('team_inst_statSupport')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* List */}
      {installers.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-slate-300 py-20 px-6 text-center">
          <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-teal-100 to-cyan-100 flex items-center justify-center text-teal-600 mb-4">
            <IconSearch className="w-9 h-9" />
          </div>
          <h3 className="text-lg font-bold text-slate-800">{t('team_inst_emptyTitle')}</h3>
          <p className="text-sm text-slate-500 mt-2 max-w-sm mx-auto mb-6">
            {t('team_inst_emptyText')}
          </p>
          <button
            onClick={() => setShowModal(true)}
            className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-white font-semibold shadow-lg shadow-cyan-500/30 inline-flex items-center gap-2"
          >
            <IconSearch className="w-4 h-4" /> {t('team_inst_browse')}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {installers.map(inst => {
            const initials = inst.name.slice(0, 2).toUpperCase();
            return (
              <div key={inst.linkId} className="group relative bg-white rounded-2xl border border-slate-200 hover:border-teal-300 hover:shadow-xl hover:shadow-teal-500/10 hover:-translate-y-0.5 transition-all overflow-hidden">
                <div className="h-1 bg-gradient-to-r from-teal-400 via-cyan-400 to-sky-400" />
                <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-gradient-to-br from-teal-400 to-cyan-400 opacity-0 group-hover:opacity-10 blur-2xl transition-opacity" />
                <div className="relative p-5">
                  <div className="flex items-start gap-4">
                    <div className="relative shrink-0">
                      <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-teal-400 to-cyan-500 blur opacity-40 group-hover:opacity-70 transition-opacity" />
                      <div className="relative w-14 h-14 rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white font-bold shadow-sm ring-1 ring-white/40">
                        {initials}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-slate-900 text-lg truncate group-hover:text-teal-700 transition-colors">{inst.name}</h3>
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {inst.speciality && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-teal-50 text-teal-700 border border-teal-100">
                            <IconWrench className="w-3 h-3" />
                            {inst.speciality}
                          </span>
                        )}
                        {inst.city && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-600">
                            <IconMapPin className="w-3 h-3" />
                            {inst.city}
                          </span>
                        )}
                      </div>
                      {inst.contactEmail && (
                        <p className="text-xs text-slate-500 mt-2 truncate inline-flex items-center gap-1.5">
                          <IconMail className="w-3.5 h-3.5 text-slate-400" />
                          {inst.contactEmail}
                        </p>
                      )}
                      <p className="text-[11px] text-slate-400 mt-1">
                        {t('team_inst_linkedSince', { date: new Date(inst.addedAt).toLocaleDateString(locale, { day: '2-digit', month: 'long', year: 'numeric' }) })}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
                    {inst.contactEmail && (
                      <a
                        href={`mailto:${inst.contactEmail}`}
                        className="flex-1 px-3 py-2 text-sm font-medium text-center text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-lg transition-colors inline-flex items-center justify-center gap-1.5"
                      >
                        <IconMail className="w-4 h-4" /> {t('team_inst_contact')}
                      </a>
                    )}
                    <button
                      onClick={() => { setRevokeError(''); setRevokeTarget(inst); }}
                      className="px-3 py-2 text-sm font-medium text-rose-600 bg-rose-50 hover:bg-rose-100 rounded-lg transition-colors inline-flex items-center gap-1.5"
                    >
                      <IconTrash className="w-4 h-4" /> {t('team_inst_remove')}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <AddInstallerModal
          token={token!}
          apiUrl={API}
          onClose={() => setShowModal(false)}
          onAdded={() => { setShowModal(false); fetchInstallers(); }}
        />
      )}

      {revokeTarget && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => !revoking && setRevokeTarget(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="bg-gradient-to-br from-rose-600 to-red-700 p-6 text-white">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center text-white shrink-0">
                  <IconAlertTriangle className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">{t('team_inst_revokeTitle')}</h2>
                  <p className="text-sm text-rose-100 mt-1">
                    {t('team_inst_revokeSubtitle')}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 bg-slate-50">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white font-bold shrink-0">
                  {revokeTarget.name.slice(0, 2).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 truncate">{revokeTarget.name}</p>
                  <p className="text-xs text-slate-500 truncate">
                    {[revokeTarget.city, revokeTarget.speciality].filter(Boolean).join(' • ') || '—'}
                  </p>
                </div>
              </div>

              <div className="text-sm text-slate-600 space-y-2">
                <p className="font-semibold text-slate-800">{t('team_inst_consequences')}</p>
                <ul className="list-disc pl-5 space-y-1 text-slate-600">
                  <li>{t('team_inst_cons1')}</li>
                  <li>{t('team_inst_cons2')}</li>
                  <li>{t('team_inst_cons3')}</li>
                </ul>
                <p className="text-xs text-slate-500 italic pt-1">
                  {t('team_inst_reinvite')}
                </p>
              </div>

              {revokeError && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                  {revokeError}
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => setRevokeTarget(null)}
                  disabled={revoking}
                  className="px-4 py-2 text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 font-medium text-sm disabled:opacity-50"
                >
                  {t('team_cancel')}
                </button>
                <button
                  onClick={() => handleRevoke(revokeTarget.linkId)}
                  disabled={revoking}
                  className="px-5 py-2 bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-500 hover:to-red-500 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-sm shadow inline-flex items-center gap-2"
                >
                  {revoking ? (
                    <>
                      <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                      {t('team_inst_removing')}
                    </>
                  ) : (
                    <>
                      <IconTrash className="w-4 h-4" /> {t('team_inst_confirmRemove')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AddInstallerModal({
  token,
  apiUrl,
  onClose,
  onAdded,
}: {
  token: string;
  apiUrl: string;
  onClose: () => void;
  onAdded: () => void;
}) {
  const { t } = useI18n();
  const [directory, setDirectory] = useState<DirectoryInstaller[]>([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<DirectoryInstaller | null>(null);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { fetchDirectory(); }, [search]);

  async function fetchDirectory() {
    const res = await fetch(`${apiUrl}/api/installers/directory?search=${encodeURIComponent(search)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    if (data.success) setDirectory(data.installers);
  }

  async function handleSubmit() {
    if (!selected || !consent) return;
    setSubmitting(true);
    setError('');

    const res = await fetch(`${apiUrl}/api/my-installers`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ installerId: selected.id, consent: true }),
    });
    const data = await res.json();

    if (data.success) {
      onAdded();
    } else {
      setError(typeof data.error === 'string' ? data.error : data.error?.message || JSON.stringify(data.error) || t('team_error'));
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="bg-gradient-to-br from-slate-900 to-teal-900 p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">{t('team_inst_add')}</h2>
              <p className="text-sm text-slate-300 mt-1">{t('team_inst_modalSubtitle')}</p>
            </div>
            <button onClick={onClose} aria-label={t('team_close')} className="text-white/60 hover:text-white text-2xl leading-none">&times;</button>
          </div>
        </div>

        <div className="p-6">
          <div className="relative mb-4">
            <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder={t('team_inst_searchPh')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
            />
          </div>

          <div className="max-h-56 overflow-y-auto border border-slate-200 rounded-lg mb-4 divide-y divide-slate-100">
            {directory.length === 0 ? (
              <p className="p-6 text-slate-500 text-sm text-center">{t('team_inst_noneFound')}</p>
            ) : (
              directory.map(inst => (
                <label
                  key={inst.id}
                  className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors ${selected?.id === inst.id ? 'bg-teal-50' : ''}`}
                >
                  <input
                    type="radio"
                    name="installer"
                    checked={selected?.id === inst.id}
                    onChange={() => setSelected(inst)}
                    className="text-teal-600 focus:ring-teal-500"
                  />
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
                    {inst.name.slice(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-900 truncate">{inst.name}</p>
                    <p className="text-xs text-slate-500 truncate">
                      {[inst.city, inst.speciality].filter(Boolean).join(' • ') || '—'}
                    </p>
                  </div>
                </label>
              ))
            )}
          </div>

          <label className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4 cursor-pointer">
            <input
              type="checkbox"
              checked={consent}
              onChange={e => setConsent(e.target.checked)}
              className="mt-1 text-teal-600 focus:ring-teal-500"
            />
            <div className="text-sm text-slate-700">
              <p className="font-semibold mb-1">{t('team_inst_consentTitle')}</p>
              <ul className="list-disc pl-4 space-y-0.5 text-slate-600 text-xs">
                <li>{t('team_inst_consent1')}</li>
                <li>{t('team_inst_consent2')}</li>
                <li>{t('team_inst_consent3')}</li>
              </ul>
              <p className="mt-2 text-slate-500 italic text-xs">{t('team_inst_consentRevocable')}</p>
            </div>
          </label>

          {error && <p className="text-red-600 text-sm mb-3 p-3 bg-red-50 rounded-lg border border-red-200">{error}</p>}

          <div className="flex justify-end gap-3">
            <button onClick={onClose} className="px-4 py-2 text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 font-medium text-sm">
              {t('team_cancel')}
            </button>
            <button
              onClick={handleSubmit}
              disabled={!selected || !consent || submitting}
              className="px-5 py-2 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-500 hover:to-cyan-500 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-sm shadow"
            >
              {submitting ? t('team_inst_adding') : t('team_inst_addBtn')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
